import './style.css';

/**
 * Lead Capture – Frontend HubSpot integration.
 *
 * For blocks with a linked guide (data-guide-id), this script:
 * 1. Reads server-injected guide data (JSON) from the block HTML.
 * 2. Listens for HubSpot hsFormCallback window messages.
 * 3. On "onFormReady" — populates hidden form fields with guide data.
 * 4. On "onFormSubmitted" — shows a loading state then redirects to
 *    /thank-you/?ty=guide&guide_id={id}
 *
 * Mirrors the logic in single-guide.php and template-parts/landing/form.php.
 */

interface GuideData {
  id: number;
  name: string;
  fileUrl: string;
  imageUrl: string;
  thankYouUrl: string;
  loadingText: string;
}

interface HsFormCallbackData {
  type: 'hsFormCallback';
  eventName: string;
  id?: string;
}

interface HsFormCallbackEvent extends MessageEvent {
  data: HsFormCallbackData;
}

/**
 * Populate hidden fields in a HubSpot form embedded within or near
 * the given block. Searches both the block DOM (for inline embeds)
 * and any HubSpot iframes on the page.
 */
function populateFields(block: HTMLElement, fields: Record<string, string | number>): void {
  Object.entries(fields).forEach(([name, value]) => {
    // Try inline form first (non-iframe embed)
    let input = block.querySelector<HTMLInputElement>(`input[name="${name}"]`);

    // Fallback: search all HubSpot iframes on the page
    if (!input) {
      const iframes = block.querySelectorAll<HTMLIFrameElement>('iframe');
      for (const iframe of iframes) {
        try {
          const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
          if (iframeDoc) {
            input = iframeDoc.querySelector<HTMLInputElement>(`input[name="${name}"]`);
            if (input) break;
          }
        } catch {
          // Cross-origin iframe — can't access, skip
        }
      }
    }

    // Last resort: search entire document (for forms rendered outside block)
    if (!input) {
      input = document.querySelector<HTMLInputElement>(`input[name="${name}"]`);
    }

    if (input) {
      input.value = String(value);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  });
}

function initLeadCapture(block: HTMLElement): void {
  const scriptEl = block.querySelector<HTMLScriptElement>(
    'script.lead-capture-guide-data[type="application/json"]',
  );
  if (!scriptEl) return;

  let guideData: GuideData;
  try {
    guideData = JSON.parse(scriptEl.textContent || '');
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[LeadCapture] Failed to parse guide data:', e);
    return;
  }

  if (!guideData.id) {
    // eslint-disable-next-line no-console
    console.warn('[LeadCapture] Guide data missing id');
    return;
  }

  const guideFields: Record<string, string | number> = {
    form___guide_name: guideData.name,
    form_guide_url: guideData.fileUrl,
    guide_image_url: guideData.imageUrl,
  };

  let fieldsPopulated = false;
  let submitted = false;

  function handleFormReady(): void {
    if (fieldsPopulated) return;

    populateFields(block, guideFields);
    fieldsPopulated = true;
  }

  function handleFormSubmitted(): void {
    if (submitted) return;
    submitted = true;

    // Show branded loading state — find the form container
    const formCard =
      block.querySelector<HTMLElement>('.hbspt-form') ||
      block.querySelector<HTMLElement>('.hs-form-iframe') ||
      block.querySelector<HTMLElement>('iframe')?.parentElement ||
      block.querySelector<HTMLElement>('.landing-page__form-card') ||
      block.querySelector<HTMLElement>('.pardot-form');

    if (formCard) {
      const loading = document.createElement('div');
      loading.className = 'form-redirect-loading';

      const icon = document.createElement('div');
      icon.className = 'form-redirect-loading__icon';
      icon.innerHTML =
        '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="28" height="28"><path d="M9.86 18a1 1 0 0 1-.73-.32l-4.86-5.17a1 1 0 1 1 1.46-1.37l4.12 4.39 8.41-9.2a1 1 0 1 1 1.48 1.34l-9.14 10a1 1 0 0 1-.73.33Z"/></svg>';

      const text = document.createElement('p');
      text.className = 'form-redirect-loading__text';
      text.textContent = guideData.loadingText;

      loading.appendChild(icon);
      loading.appendChild(text);

      formCard.replaceChildren(loading);
    }

    let redirectUrl = guideData.thankYouUrl + '?ty=guide&guide_id=' + guideData.id;

    if (guideData.imageUrl) {
      redirectUrl += '&guide_image=' + encodeURIComponent(guideData.imageUrl);
    }

    // Brief delay so user sees the animated tick before redirect
    setTimeout(() => {
      window.location.href = redirectUrl;
    }, 1200);
  }

  // Listen for HubSpot form events
  window.addEventListener('message', (event: HsFormCallbackEvent) => {
    if (!event.data || event.data.type !== 'hsFormCallback') return;

    if (event.data.eventName === 'onFormReady') {
      handleFormReady();
    }

    if (event.data.eventName === 'onFormSubmitted') {
      handleFormSubmitted();
    }
  });

  // Retry field population — handles race condition where onFormReady
  // fires before our listener is attached. Polls for the form inputs
  // and populates them once found.
  let retryCount = 0;
  const maxRetries = 20;
  const retryInterval = setInterval(() => {
    retryCount++;

    if (fieldsPopulated || retryCount >= maxRetries) {
      clearInterval(retryInterval);
      return;
    }

    // Check if form inputs already exist
    const testInput =
      block.querySelector<HTMLInputElement>('input[name="form___guide_name"]') ||
      document.querySelector<HTMLInputElement>('input[name="form___guide_name"]');

    if (testInput) {
      handleFormReady();
      clearInterval(retryInterval);
    }
  }, 500);
}

// Initialize all lead-capture blocks with a guide
function init(): void {
  const blocks = document.querySelectorAll<HTMLElement>(
    '.wp-block-interact-lead-capture[data-guide-id]',
  );
  blocks.forEach(initLeadCapture);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
